import React from "react";
import { Box } from "@mui/material";
import DoneSharpIcon from '@mui/icons-material/DoneSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import PaymentIcon from "../utils/images/payment-02.svg";
import AlertIcon from "../utils/images/alert-circle.svg";

export const TimelineDot = ({ status, stepTitle }) => {
  const isPaymentSettled = stepTitle === "Payment Settled" && status === "current";
  const isRejected = stepTitle === "Rejected" && status === "current";

  if (isPaymentSettled || isRejected) {
    return (
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          backgroundColor: "#F0F3F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
         {isRejected ? (
          <ClearSharpIcon sx={{ color: "#2E3742", fontSize: 23 }} />
        ) : (
          <DoneSharpIcon sx={{ color: "#2E3742", fontSize: 23 }} />
        )}
      </Box>
    );
  }

  const getStyles = () => {
    switch(status) {
      case 'completed':
        return {
          width: 12,
          height: 12,
          bgcolor: '#47566A',
          border: 'none',
        };
      case 'current':
        return {
          width: 12,
          height: 12,
          bgcolor: '#2E3742',
        };
      default:
        return {
          width: 12,
          height: 12,
          bgcolor: '#E5E7EB',
          border: 'none',
        };
    }
  };

  return (
    <Box
      sx={{
        ...getStyles(),
        borderRadius: '50%',
        position: 'relative',
        zIndex: 2,
        flexShrink: 0,
        ...(status === 'current' && {
          animation: 'innerPulse 2s ease-in-out infinite',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 26,
            height: 26,
            borderRadius: '50%',
            border: '2px dashed #2E3742',
            opacity: 0.6,
            animation: 'breathingBorder 2s ease-in-out infinite',
          },
          '@keyframes innerPulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              boxShadow: '0 0 0 0 rgba(46, 55, 66, 0.3)',
            },
            '50%': {
              transform: 'scale(1.1)',
              boxShadow: '0 0 0 4px rgba(46, 55, 66, 0.1)',
            },
          },
          '@keyframes breathingBorder': {
            '0%, 100%': {
              transform: 'translate(-50%, -50%) scale(1)',
              opacity: 0.6,
              borderWidth: '2px',
            },
            '50%': {
              transform: 'translate(-50%, -50%) scale(1.1)',
              opacity: 0.9,
              borderWidth: '2.5px',
            },
          },
        })
      }}
    />
  );
};
  
export const TimelineConnector = ({ status, connectingToCurrent, nextStepTitle}) => {
    const isConnectingToFinalStep = nextStepTitle === "Payment Settled" || nextStepTitle === "Rejected";
    return (
      <Box
        sx={{
          position: 'absolute',
          top: status === 'current' ? 19 : 12,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 2,
           height: isConnectingToFinalStep 
          ? { xs: 'calc(100% + 41px)', sm: 'calc(100% + 49px)' } 
          : connectingToCurrent ? {xs:'calc(100% + 30px)',sm:'calc(100% + 38px)'} : {xs:'calc(100% + 40px)',sm:'calc(100% + 47px)'},
          background: '#2E3742',
        }}  
      />
    );
  };

export const getStatusConfig = (displayStatus) => {
    const configs = {
      "Submitted": {
        title: "Claim Submitted",
        description: "Your claim has been submitted successfully. We'll start reviewing it shortly.",
        icon: <ThumbUpAltOutlinedIcon sx={{ fontSize: 23 }} />,
        bgColor: "#FAFAFF",
        iconColor: "#5E40AD",
      },
      "Under Process": {
        title: "Under Process",
        description: "Our team is carefully reviewing your claim. We'll update you once the assessment is complete.",
        icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 23 }} />,
        bgColor: "#F1F7FF",
        iconColor: "#004FB6",
      },
      "Information Required": {
        title: "On Hold - Information Required",
        description: "We need a few more details/documents to proceed with your claim. Please upload them to avoid delays.",
        icon:<img src={AlertIcon} width={24} height={24} alt="alert" />,
        bgColor: "#FFFAF6",
        iconColor: "#AB5200",
      },
      "Approved": {
        title: "Claim Approved",
        description: "Your claim has been fully approved. The settlement is being processed.",
        icon: <DoneSharpIcon sx={{ fontSize: 24 }} />,
        bgColor: "#F8FBFA",
        iconColor: "#0F7A48",
      },
      "Partially Approved": {
        title: "Claim Partially Approved",
        description: "A part of your claim has been approved. Check the details for the approved amount.",
        icon: <DoneSharpIcon sx={{ fontSize: 24 }} />,
        bgColor: "#F8FBFA",
        iconColor: "#0F7A48",
      },
      "Rejected": {
        title: "Claim Rejected",
        description: "We're sorry, but your claim has been rejected. Please review the remarks for more details.",
        icon: <ClearSharpIcon sx={{ fontSize: 23 }} />,
        bgColor: "#FFF4F5",
        iconColor: "#AE0006",
      },
      "Raised For Payment": {
        title: "Raised for Payment",
        description: "Your claim has been approved and raised for payment. The amount will be transferred soon.",
        icon: <img src={PaymentIcon} width={24} height={24} alt="payment" />,
        bgColor: "#F1F7FF",
        iconColor: "#004FB6",
      },
      "Payment Settled": {
        title: "Payment Settled",
        description: "Great news! Your claim payment has been settled and the amount will reflect in your account shortly.",
        icon: <DoneSharpIcon sx={{ fontSize: 23 }} />,
        bgColor: "#F8FBFA",
        iconColor: "#0F7A48",
      },
    };

    return configs[displayStatus] || {
      title: "Claim Processing",
      description: "Your claim is being processed. We'll update you soon.",
      icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 23 }} />,
      bgColor: "#F1F7FF",
      iconColor: "#004FB6",
    };
  };